import React, { useMemo, useState, forwardRef } from 'react';
import { Form } from 'components/atoms';
import { Formik, Field } from 'formik';
import { FieldCustom, FieldArrayDndFormik } from 'components/molecules';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useDebounce } from 'react-use';
import { useFormikForwardRef } from 'hooks';
import { linkRegex } from 'services/constants'
import { generateKey } from 'services/generators'

const ItemSchema = Yup.object().shape({
  info: Yup.object().shape({
    value: Yup.string()
      .min(2, 'Mínimo de 2 caracteres')
      .max(120, 'Máximo de 120 caracteres')
      .required('Campo Obrigatório'),
  }),
  href: Yup.string()
    .matches(/^(https?:\/\/)?(\s*)?[-a-zA-Z0-9_@:?&%.+~#=/]*$/, 'Url inválido')
    .nullable()
    .notRequired(),
  extra: Yup.string()
    .matches(linkRegex, 'O URL inserido não é válido. Certifique-se que começa com http:// ou https:// para sites fora do portal ou com /{lingua}/... para ligações internas.')
    .required('Campo obrigatório'),
});

const SubWebLinksSchema = Yup.object().shape({
  title: Yup.string()
    .min(2, 'Mínimo de 2 caracteres')
    .max(120, 'Máximo de 120 caracteres')
    .required('Campo Obrigatório'),
  data: Yup.array().of(ItemSchema).required('Campo obrigatório'),
});


// eslint-disable-next-line react/prop-types
const LinksForm = forwardRef(({ initialValues, onSubmit }, ref) => {
  // this is necessary for all FieldArrayDnd forms
  // in order to control the submitForm from parent
  const formRef = useFormikForwardRef(ref);

  const initialValuesComputed = useMemo(() => {
    const { info, extra } = initialValues
    return { info, extra }
  }, [initialValues])

  const formToModuleSchema = (values) => values;

  return (
    <Formik
      ref={formRef}
      initialValues={initialValuesComputed}
      validationSchema={ItemSchema}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(formToModuleSchema(values));
        setSubmitting(false);
      }}
      validateOnMount
      enableReinitialize
    >
      <Form>
        <Field
          title="Texto"
          name="info.value"
          info="Texto do link"
          component={FieldCustom}
        />
        <Field
          title="URL"
          name="extra"
          info="Link para página"
          component={FieldCustom}
        />
      </Form>
    </Formik>
  )
})

const SubWebLinksEditor = ({ initialProps, onChange }) => {
  const [editorProps, setEditorProps] = useState(initialProps)

  const initialValues = useMemo(() => {
    const { title, href, data } = initialProps;
    return { title, data, href };
  }, [initialProps]);

  const handleFormChange = newValues => {
    const newProps = { ...initialProps, ...newValues };
    const isValid = SubWebLinksSchema.isValidSync(newProps)
    if (isValid) setEditorProps(newProps)
  };

  useDebounce(() => {
    onChange(editorProps);
  }, 200,
  [editorProps]
  )

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={SubWebLinksSchema}
      validate={handleFormChange}
    >
      <Form>
        <Field
          title="Título"
          name="title"
          info="Título do módulo"
          component={FieldCustom}
        />
        <Field
          title="Link (opcional)"
          name="href"
          placeholder="/pt/"
          info="Link de destino"
          component={FieldCustom}
        />
        <Field
          name="data"
          component={FieldArrayDndFormik}
          columns={[
            {
              accessor: 'info.value',
              Header: 'Texto',
            },
            {
              accessor: 'extra',
              Header: 'URL',
            }
          ]}
          rowKey={(item) => generateKey(`${item.info.value}-${item.extra}`)}
          ItemForm={LinksForm}
        />
      </Form>
    </Formik>
  );
};

SubWebLinksEditor.propTypes = {
  onChange: PropTypes.func.isRequired,
  initialProps: PropTypes.shape({
    href: PropTypes.string,
    title: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({
      info: PropTypes.shape({
        value: PropTypes.string,
      }),
      extra: PropTypes.string,
    }))
  }),
};

SubWebLinksEditor.defaultProps = {
  initialProps: {},
};


export default SubWebLinksEditor;
